// ---------------------------------------------------------------------------
// Provider Data Model
//
// Three core entities: Organization, Practitioner, Affiliation
// Organizations are the primary browsable entity (clinics, practices, labs).
// Practitioners are individual people who may work at multiple organizations.
// Affiliations link practitioners to organizations with role context.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Organization — the clinic, practice, lab, or wellness provider
// ---------------------------------------------------------------------------

export type OrganizationType =
  | "fertility-clinic"
  | "medical-practice"
  | "solo-practitioner"
  | "wellness-provider"
  | "lab"
  | "pharmacy"
  | "donor-agency"
  | "surrogacy-agency";

/**
 * Types that appear in the FindCare screen.
 * Pharmacy, donor-agency, and surrogacy-agency get their own dedicated pages.
 */
export const FINDCARE_TYPES: OrganizationType[] = [
  "fertility-clinic",
  "medical-practice",
  "solo-practitioner",
  "wellness-provider",
  "lab",
];

/**
 * Practice setting — how the clinic is organized, independent of its medical
 * `type`. Patients filter on this to choose between a small independent
 * practice, a hospital/academic program, or a multi-site commercial network.
 * Values reuse the legacy clinic-type taxonomy (Boutique / Hospital-based /
 * Network & group). Derived from `network` membership + name signals; see
 * scripts/providers/classify-clinic-setting.ts.
 */
export type ClinicSetting = "boutique" | "hospital-based" | "network-group";

export const CLINIC_SETTING_LABELS: Record<ClinicSetting, string> = {
  boutique: "Boutique",
  "hospital-based": "Hospital-based",
  "network-group": "Network & group",
};

/** Display order for the FindCare setting filter. */
export const CLINIC_SETTING_OPTIONS: ClinicSetting[] = [
  "boutique",
  "hospital-based",
  "network-group",
];

/**
 * Pharmacy-only operational facts published on the pharmacy's own site.
 * Present only on records with `type: "pharmacy"`.
 */
export type PharmacyDetails = {
  /** In-house sterile / BHRT compounding capability. */
  compoundingOnSite?: boolean;
  /** Shipment logistics. */
  delivery?: PharmacyDelivery;
  /**
   * Cash-pay prices the pharmacy publishes on its own site. Only pharmacies
   * that publish a real price list (e.g., Mandell's drug-pricing calculator)
   * should have this populated — quote-form pharmacies should not.
   */
  publishedPrices?: PharmacyPublishedPrices;
};

export type PharmacyDelivery = {
  /** Guarantees temperature-controlled (cold-chain) shipping. */
  coldChain?: boolean;
  /** Cities or regions with same-day delivery. */
  sameDayCities?: string[];
  /** Ships overnight to all 50 states. */
  overnightNationwide?: boolean;
  /** Free-form note for detail the structured fields can't capture. */
  notes?: string;
};

export type PharmacyPublishedPrices = {
  /** URL where the prices were scraped. */
  sourceUrl: string;
  /** ISO date the prices were last verified. */
  verifiedOn: string;
  /** ISO 4217 currency code; defaults to USD when omitted. */
  currency?: string;
  /** Individual medication line items. */
  items: PharmacyPublishedPrice[];
};

export type PharmacyPublishedPrice = {
  /** Medication name as the pharmacy lists it (brand + strength + form). */
  medication: string;
  /** Price per unit as listed. */
  price: number;
};

export type Organization = {
  id: string;

  // External IDs for cross-referencing and lead attribution
  externalIds: {
    cdcClinicId?: string;
    sartId?: string;
    npiType2?: string;
    googlePlaceId?: string;
    abmFrId?: string;
    fivfrUrl?: string;
    wikipediaUrl?: string;
    ontarioFertilityProgramUrl?: string;
    canadaDirectoryUrl?: string;
    redlaraId?: string;
    redlaraUrl?: string;
    mexicoDirectoryUrl?: string;
    fertilityIqUrl?: string;
    hfeaClinicId?: string;
    hfeaUrl?: string;
    sefUrl?: string;
    cnpmaUrl?: string;
    belrapUrl?: string;
    dirUrl?: string;
    issPmaId?: string;
    issPmaUrl?: string;
    eaiyaUrl?: string;
    eaiyaEuTeCode?: string;
    ivfWorldwideId?: string;
    ivfWorldwideUrl?: string;
    rtacId?: string;
    yourIvfSuccessClinicId?: string;
    yourIvfSuccessUrl?: string;
    artSurrogacyRegistrationNo?: string;
    artSurrogacyCertificateUrl?: string;
    jsogFacilityId?: string;
    jsogUrl?: string;
    koreaIvfDesignatedId?: string;
    koreaEmbryoDesignationNo?: string;
    koreaDataGoKrUrl?: string;
    chinaNhcSequence?: string;
    chinaNhcUrl?: string;
    indonesiaClinicUrl?: string;
    perfitriRegistryUrl?: string;
    vietnamIvfUrl?: string;
    afrhNigeriaUrl?: string;
    rahrClinicId?: string;
    rahrUrl?: string;
  };

  // Identity
  type: OrganizationType;
  name: string;
  description?: string; // From their website, never written by us
  website?: string;
  phone?: string;
  email?: string;
  bookingUrl?: string;
  logoUrl?: string; // Direct URL to clinic's logo (SVG/PNG)
  socialLinks?: ProviderSocialLink[];

  // Location (primary)
  address: ProviderAddress;
  location?: GeoPoint;

  // Additional locations (satellite offices, branch locations)
  locations?: OrganizationLocation[];

  // Schedule
  schedule?: ProviderSchedule;

  // Treatments offered
  treatments?: ProviderTreatment[];

  // Normalized service availability, when a source explicitly verifies it.
  serviceCapabilities?: ProviderServiceCapability[];

  // Insurance & financing
  insurance?: ProviderInsurance;

  // Pricing (only for clinics with publicly listed prices)
  pricing?: ProviderPricing;

  // Country/market-specific care, payment, regulatory, and legal context.
  marketProfile?: ProviderMarketProfile;

  // Outcomes from a verified national source or clearly labeled clinic source.
  outcomes?: ProviderOutcomes;

  // Logistics
  languages?: string[];
  consultationTypes?: ConsultationType[];
  patientGroups?: string[];
  accessibilities?: string[];
  establishedYear?: number;
  isFirstConsultationFree?: boolean;

  // Military discount (only for clinics that offer one)
  militaryDiscount?: MilitaryDiscount;

  // Pharmacy-specific operational details (only populated for `type: "pharmacy"`).
  pharmacyDetails?: PharmacyDetails;

  // Media
  images?: ProviderImage[];

  // Team: denormalized from Practitioner + Affiliation for display
  team?: AffiliatedPractitioner[];

  // Service-level tags for filtering within a type (e.g. "acupuncture", "nutrition", "mental-health")
  // Distinct from `type` which determines app placement — specialties describe what the org offers.
  specialties?: string[];

  // Computed at build time from affiliated practitioners' specialties/researchFocus
  searchableSpecialties?: string[];

  // Network/parent organization
  network?: {
    name: string;
    id?: string;
  };

  // Practice setting — boutique vs hospital-based vs network group.
  // Derived from `network` + name signals; see classify-clinic-setting.ts.
  setting?: ClinicSetting;

  // Enrichment metadata
  _enrichment: EnrichmentMeta;
  _meta: RecordMeta;
};

// ---------------------------------------------------------------------------
// Practitioner — an individual healthcare provider
// ---------------------------------------------------------------------------

export type Practitioner = {
  id: string;

  externalIds: {
    npiType1?: string;
  };

  // Identity
  name: string;
  credentials?: string[]; // ["MD", "FACOG", "PhD"]
  specialty?: string; // "Reproductive Endocrinology & Infertility"
  bio?: string;
  imageUrl?: string;
  socialLinks?: ProviderSocialLink[];

  // Professional background
  boardCertifications?: string[];
  education?: PractitionerEducation[];

  // Research & publications
  researchFocus?: string[]; // ["PCOS", "male factor infertility", "endometriosis"]
  publications?: PractitionerPublication[];

  _enrichment: EnrichmentMeta;
  _meta: RecordMeta;
};

// ---------------------------------------------------------------------------
// Affiliation — links a practitioner to an organization
// ---------------------------------------------------------------------------

export type Affiliation = {
  organizationId: string;
  practitionerId: string;
  role: string; // "Medical Director", "REI", "Consulting Urologist"
  isPrimary?: boolean;
  isActive: boolean;
  _source?: FieldSource;
};

// ---------------------------------------------------------------------------
// Denormalized view: practitioner as shown on an organization's team section
// ---------------------------------------------------------------------------

export type AffiliatedPractitioner = {
  practitionerId: string;
  name: string;
  credentials?: string[];
  specialty?: string;
  role: string; // From affiliation
  imageUrl?: string;
  socialLinks?: ProviderSocialLink[];
  researchFocus?: string[];
  publicationCount?: number;
  _source?: FieldSource;
};

// ---------------------------------------------------------------------------
// Supporting types
// ---------------------------------------------------------------------------

export type ConsultationType = "in-person" | "telehealth";

export type OrganizationLocation = {
  name?: string; // e.g. "Orlando", "Tampa North"
  address: ProviderAddress;
  location?: GeoPoint;
  phone?: string;
};

export type ProviderAddress = {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

export type GeoPoint = {
  lat: number;
  lng: number;
};

export type DayHours = {
  open: string; // "08:00"
  close: string; // "17:00"
};

export type ProviderSchedule = {
  mon?: DayHours | null;
  tue?: DayHours | null;
  wed?: DayHours | null;
  thu?: DayHours | null;
  fri?: DayHours | null;
  sat?: DayHours | null;
  sun?: DayHours | null;
};

export type ProviderTreatment = {
  name: string;
  category?: "art" | "surgical" | "diagnostic" | "wellness" | "preservation";
  /** Explicit service availability when a source verifies more than presence. */
  status?: "offered" | "not-offered" | "unknown";
  priceRange?: {
    min: number;
    max: number;
    currency: string;
    notes?: string;
  };
  _source?: FieldSource;
};

/**
 * A normalized service/capability assertion. Unlike `treatments`, this can
 * represent a verified absence as well as an unconfirmed service, with
 * provenance kept beside the status.
 */
export type ProviderServiceCapability = {
  key: string;
  status: "offered" | "not-offered" | "unknown";
  verifiedAt?: string;
  _source?: FieldSource;
};

/**
 * A single service/package exactly as listed on the clinic's pricing page.
 * No normalization — capture every detail verbatim so we can compare later.
 */
export type PricingPackage = {
  name: string; // Exact service name as listed, e.g. "IVF", "Frozen Embryo Transfer"
  description?: string; // Marketing blurb / tagline if any
  price: number; // In cents to avoid floating point
  priceMax?: number; // Upper bound for ranges, e.g. "$6,900 - $9,660"
  locationPrices?: {
    location: string; // City, state, or other location label used by the clinic
    price: number;
    priceMax?: number;
  }[];
  currency: string; // "USD"
  includes?: string[]; // Verbatim bullet points of what's included
  excludes?: string[]; // Verbatim bullet points of what's excluded
  notes?: string; // Any fine print or additional context
};

/**
 * A financing option offered by the clinic — payment plans, shared-risk
 * programs, multi-cycle discounts, third-party financing, etc.
 * Each option gets its own card in the UI.
 */
export type FinancingOption = {
  name: string; // e.g. "Internal Payment Plan", "Spring Promise Shared Risk"
  description?: string; // One-line summary
  details?: string[]; // Bullet points of what's included / how it works
  eligibility?: string; // Who qualifies
  exclusions?: string; // What's not covered
  notes?: string; // Fine print
  _source?: FieldSource;
};

/**
 * Clinic pricing — a flat list of packages exactly as the clinic presents them.
 * No categories or normalization; we capture raw detail first, compare later.
 */
export type ProviderPricing = {
  packages: PricingPackage[];
  financingOptions?: FinancingOption[];
  disclaimer?: string; // Overall fine print
  _source?: FieldSource;
};

export type ProviderMarketProfileSection = {
  title: string;
  items: {
    label: string;
    value: string;
    detail?: string;
    _source?: FieldSource;
  }[];
  _source?: FieldSource;
};

export type ProviderMarketProfile = {
  country: string;
  regulatory?: {
    regulator?: string;
    licenseStatus?: string;
    licenseId?: string;
    accreditation?: string[];
    notes?: string;
    _source?: FieldSource;
  };
  payment?: {
    publicCoverage?: string;
    privatePay?: string;
    billingSector?: string;
    fundingProgram?: string;
    financing?: string;
    notes?: string;
    _source?: FieldSource;
  };
  carePathway?: {
    referralRequired?: boolean;
    waitTime?: string;
    consentRequirements?: string[];
    eligibility?: string[];
    storageRules?: string;
    notes?: string;
    _source?: FieldSource;
  };
  legal?: {
    donorAnonymity?: string;
    surrogacy?: string;
    embryoDisposition?: string;
    pgt?: string;
    ageLimits?: string;
    notes?: string;
    _source?: FieldSource;
  };
  displaySections?: ProviderMarketProfileSection[];
};

export type MilitaryDiscount = {
  summary: string; // e.g. "15% off qualifying self-pay fertility treatments"
  eligibility: string; // Who qualifies
  coverage: string; // What the discount covers
  exclusions?: string; // What it doesn't cover
  additionalPrograms?: {
    name: string;
    description: string;
    contact?: string;
  }[];
  _source?: FieldSource;
};

export type ProviderInsurance = {
  accepted: boolean;
  carriers?: string[]; // Actual insurance carrier names (e.g. "Aetna", "Cigna")
  providers?: string[]; // Legacy field — use carriers instead
  employeeBenefits?: string[];
  financingOptions?: string[];
  _source?: FieldSource;
};

export type OutcomeSourceType =
  | "national-mandatory-registry"
  | "official-national-publication"
  | "clinic-self-published"
  | "registry-participation"
  | "public-funding-program";

export type ProviderOutcomes = {
  successRates: {
    ageBand: string; // "<35", "35-37", "38-40", ">40" (CDC) or "all-ages" (ABM)
    rate: number;
    label?: string;
    treatment?: string;
    metric?: string;
    denominator?: string;
    notes?: string;
    nationalAverage?: number;
    nationalAverageLabel?: string;
  }[];
  cyclesPerYear?: number;
  reportedTo: string; // e.g. "CDC", "SART", "ABM", "HFEA", or a clinic/source label
  reportYear: number;
  sourceType?: OutcomeSourceType;
  hasOnSiteLab?: boolean;
  notes?: string;
  /** National rank when this clinic appears in an aggregator's leaderboard. */
  nationalRank?: number;
  nationalRankOutOf?: number;
  _source?: FieldSource;
};

export type ProviderImage = {
  url: string;
  type?: "exterior" | "interior" | "team" | "lab" | "logo" | "other";
  caption?: string;
  _source?: FieldSource;
};

export type ProviderSocialPlatform =
  | "instagram"
  | "youtube"
  | "facebook"
  | "tiktok"
  | "linkedin"
  | "x"
  | "whatsapp"
  | "podcast";

export type ProviderSocialLink = {
  platform: ProviderSocialPlatform;
  url: string;
  label?: string;
  _source?: FieldSource;
};

export type PractitionerEducation = {
  school: string;
  degree: string;
  year?: number;
};

export type PractitionerPublication = {
  title: string;
  journal?: string;
  year?: number;
  url?: string;
  _source?: FieldSource;
};

// ---------------------------------------------------------------------------
// Enrichment & provenance tracking
// ---------------------------------------------------------------------------

export type FieldSource = {
  url: string;
  fetchedAt: string; // ISO date
};

// "missing"         — not yet searched; we don't know if data exists.
// "searched-absent" — searched the clinic's site/sources; it does not publish
//                     this (e.g. no public pricing). A real finding, not a gap.
// "scraped"         — found and captured from a source.
// "verified"        — captured and confirmed accurate.
export type EnrichmentSectionStatus =
  "missing" | "searched-absent" | "scraped" | "verified";

export type EnrichmentMeta = {
  status: "seed-only" | "partially-enriched" | "enriched" | "verified";

  // Per-section tracking so the enrichment pipeline knows what to fetch next
  sections: Record<
    string,
    {
      status: EnrichmentSectionStatus;
      source?: FieldSource;
    }
  >;

  lastEnrichmentRun?: string;
};

export type RecordMeta = {
  createdAt: string;
  updatedAt: string;
};

// ---------------------------------------------------------------------------
// User interactions
// ---------------------------------------------------------------------------

export type UserProviderInteraction = {
  id: string;
  userId: string;
  providerId: string;
  providerType: "organization" | "practitioner";
  isSaved: boolean;
  status?: "researching" | "contacted" | "consulted" | "in-treatment";
  notes?: string;
  createdAt: string;
  updatedAt: string;
};

// ---------------------------------------------------------------------------
// Lead tracking events
// ---------------------------------------------------------------------------

export type ProviderEventType =
  | "viewed"
  | "clicked-book"
  | "clicked-call"
  | "clicked-website"
  | "clicked-directions"
  | "saved"
  | "unsaved"
  | "shared";

export type UserProviderEvent = {
  userId: string;
  providerId: string;
  providerType: "organization" | "practitioner";
  event: ProviderEventType;
  timestamp: string;
  sessionId?: string;
  metadata?: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Filters for searching/browsing organizations
// ---------------------------------------------------------------------------

export type ProviderFilters = {
  search?: string;
  type?: OrganizationType[];
  settings?: ClinicSetting[]; // Practice setting: boutique / hospital-based / network group
  userLocation?: GeoPoint;
  maxDistance?: number; // Miles
  treatments?: string[];
  specialties?: string[]; // Matches searchableSpecialties
  consultationTypes?: ConsultationType[];
  insuranceAccepted?: boolean;
  carriers?: string[]; // Specific insurance carriers, e.g. "Aetna", "Cigna"
  employeeBenefits?: string[]; // Fertility benefit programs, e.g. "Progyny"
  userInsuranceCarrier?: string; // Patient's specific carrier for smart matching
  languages?: string[];
  patientGroups?: string[];
  hasReportedOutcomes?: boolean;
  country?: string[];
};
